import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ReactGA from "react-ga4";
import { usePostHog } from "posthog-js/react";
import { sizeNameMap } from "../utils/sizeNameUtils";
import { useData } from "../contexts/DataContext";

const PriceHistoryGraph = ({ scientificName, productBucketKey, lookbackDays = 90 }) => {
  const [priceData, setPriceData] = useState([]);
  const [seriesData, setSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { BACKEND_URL } = useData();
  const isProductHistory = Boolean(productBucketKey);
  const historyLabel = productBucketKey || scientificName;

  // Get PostHog instance for analytics
  const posthog = usePostHog();

  // Function to fetch price history data
  const fetchPriceHistory = useCallback(async () => {
    if (!historyLabel) {
      setError("Missing required data to fetch price history");
      return;
    }

    setLoading(true);
    setError(null);

    // Track the request with Google Analytics
    ReactGA.event({
      category: "Price History",
      action: "Fetch Price History",
      label: historyLabel,
    });

    // Track with PostHog
    if (posthog) {
      posthog.capture("price_history_requested", {
        scientific_name: scientificName || null,
        product_bucket_key: productBucketKey || null,
        domain_type: isProductHistory ? "product" : "livestock",
        lookback_days: lookbackDays,
      });
    }

    try {
      const url = isProductHistory
        ? `${BACKEND_URL}/products/history/${encodeURIComponent(
            productBucketKey
          )}?lookback_days=${lookbackDays}`
        : `${BACKEND_URL}/historical_prices/graph/${encodeURIComponent(
            scientificName
          )}?lookback_days=${lookbackDays}`;
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch price history: ${response.statusText}`
        );
      }

      const result = await response.json();
      setPriceData(result.data || []);
      setSeriesData(result.series || []);

      // Track successful data load
      if (posthog) {
        posthog.capture("price_history_loaded", {
          scientific_name: scientificName || null,
          product_bucket_key: productBucketKey || null,
          domain_type: isProductHistory ? "product" : "livestock",
          lookback_days: lookbackDays,
          data_points: result.data?.length || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching price history:", err);
      setError(err.message);

      // Track error
      if (posthog) {
        posthog.capture("price_history_error", {
          scientific_name: scientificName || null,
          product_bucket_key: productBucketKey || null,
          domain_type: isProductHistory ? "product" : "livestock",
          lookback_days: lookbackDays,
          error: err.message,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, historyLabel, isProductHistory, lookbackDays, posthog, productBucketKey, scientificName]);

  // Fetch data when component mounts or parameters change
  useEffect(() => {
    fetchPriceHistory();
  }, [fetchPriceHistory]);

  // Process data for the chart
  const chartData = useMemo(() => {
    if (!priceData.length) return [];

    if (isProductHistory) {
      return [];
    }

    // Group data by date
    const dateGroups = {};
    priceData.forEach((item) => {
      // Filter out sizes that are not numeric and not in sizeNameMap
      if (!(!isNaN(item.size) || sizeNameMap[item.size])) {
        return;
      }

      const date = item.date;
      if (!dateGroups[date]) {
        dateGroups[date] = { date };
      }
      dateGroups[date][`size_${item.size}`] = item.price;
    });

    // Convert to array and sort by date
    return Object.values(dateGroups).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  }, [priceData, isProductHistory]);

  const productSeriesByKey = useMemo(() => {
    const map = {};
    (seriesData || []).forEach((series) => {
      if (series.series_key) map[series.series_key] = series;
    });
    return map;
  }, [seriesData]);

  const productChartGroups = useMemo(() => {
    if (!isProductHistory || !priceData.length) return [];

    const groups = {};
    (priceData || []).forEach((item) => {
      const series = productSeriesByKey[item.series_key];
      const price = item.price ?? item.daily_lowest_price;
      if (!series || !item.date || price == null) return;
      const groupKey = series.price_basis || "shelf_price";
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: series.price_basis_label || "Price",
          series: {},
          dates: {},
        };
      }
      groups[groupKey].series[item.series_key] = series;
      if (!groups[groupKey].dates[item.date]) {
        groups[groupKey].dates[item.date] = { date: item.date };
      }
      groups[groupKey].dates[item.date][item.series_key] = price;
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        series: Object.values(group.series).sort((a, b) => {
          if (a.series_type !== b.series_type) {
            return a.series_type === "overall" ? -1 : 1;
          }
          return (a.series_label || "").localeCompare(b.series_label || "");
        }),
        data: Object.values(group.dates).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        ),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isProductHistory, priceData, productSeriesByKey]);

  // Get unique sizes for the legend
  const sizes = useMemo(() => {
    if (isProductHistory) {
      return [];
    }

    const sizeSet = new Set();
    priceData.forEach((item) => {
      sizeSet.add(item.size);
    });
    return Array.from(sizeSet).sort((a, b) => {
      // Sort sizes numerically where possible
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b);
    });
  }, [priceData, isProductHistory]);

  // Colors for different size lines
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#8dd1e1",
    "#d084d0",
    "#ffb347",
    "#87d068",
    "#ff6b6b",
    "#4ecdc4",
    "#45b7d1",
    "#96ceb4",
    "#ffeaa7",
    "#dda0dd",
    "#98d8c8",
  ];

  // Format size name for display
  const formatSizeName = (size) => {
    return sizeNameMap[size] || size;
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow">
          <p className="font-medium">{`Date: ${new Date(
            label
          ).toLocaleDateString()}`}</p>
          {payload.map((entry) => {
            if (isProductHistory) {
              const series = productSeriesByKey[entry.dataKey];
              return (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                  {`${series?.series_label || "Lowest price"}: $${entry.value?.toFixed(2)}`}
                </p>
              );
            }

            const size = entry.dataKey.replace("size_", "");
            return (
              <p key={entry.dataKey} style={{ color: entry.color }}>
                {`${formatSizeName(size)}: $${entry.value?.toFixed(2)}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600 mt-2">Loading price history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-red-600">
            Failed to load price history: {error}
          </p>
          <button
            onClick={fetchPriceHistory}
            className="mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if ((!isProductHistory && !chartData.length) || (isProductHistory && !productChartGroups.length)) {
    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-gray-600">
            No price history data available for the last {lookbackDays} days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 p-2 bg-white rounded-lg shadow border">
      <h3 className="text-base font-semibold text-gray-800 mb-3 text-center">
        Price History - Last {lookbackDays} Days
      </h3>

      {isProductHistory ? (
        <div className="space-y-4">
          {productChartGroups.map((group) => (
            <div key={group.key}>
              <h4 className="text-sm font-semibold text-gray-700 text-center mb-2">
                {group.label}
              </h4>
              <div className="w-full h-56">
                <ResponsiveContainer>
                  <LineChart data={group.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) =>
                        new Date(date).toLocaleDateString("en-US", {
                          month: "numeric",
                          day: "numeric",
                        })
                      }
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={["dataMin - 5", "dataMax + 5"]}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={(value) =>
                        productSeriesByKey[value]?.series_label || value
                      }
                    />
                    {group.series.slice(0, 10).map((series, index) => (
                      <Line
                        key={series.series_key}
                        type="monotone"
                        dataKey={series.series_key}
                        stroke={colors[index % colors.length]}
                        strokeWidth={series.series_type === "overall" ? 3 : 2}
                        dot={{ r: 3 }}
                        connectNulls={false}
                        name={series.series_label}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full h-48">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) =>
                  new Date(date).toLocaleDateString("en-US", {
                    month: "numeric",
                    day: "numeric",
                  })
                }
                interval="preserveStartEnd"
              />
              <YAxis
                domain={["dataMin - 5", "dataMax + 5"]}
                tickFormatter={(value) => `$${value.toFixed(0)}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value) => formatSizeName(value.replace("size_", ""))}
              />
              {sizes
                .filter((size) => !isNaN(size) || sizeNameMap[size])
                .map((size, index) => (
                  <Line
                    key={size}
                    type="monotone"
                    dataKey={`size_${size}`}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    name={`size_${size}`}
                  />
                ))
                .slice(0, 10)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        {isProductHistory
          ? "Shows daily lowest prices for useful product property combinations."
          : "Shows the daily lowest prices for each size. Lines may have gaps when no data is available for certain dates."}
      </div>
    </div>
  );
};

export default PriceHistoryGraph;
